// src/components/upload/UploadDropzone.jsx
import React from "react";

export default function UploadDropzone({
  accept = ".csv",
  maxSizeMB = 10,
  onFiles = () => {},
  className = "",
  tone = "default",
  children,
}) {
  const inputRef = React.useRef(null);
  const [isOver, setIsOver] = React.useState(false);
  const [errors, setErrors] = React.useState([]);
  const [files, setFiles] = React.useState([]);

  const maxBytes = maxSizeMB * 1024 * 1024;
  const isSurfaceTone = tone === "surface";

  const validate = (list) => {
    const errs = [];
    const ok = [];
    Array.from(list || []).forEach((f) => {
      if (f.size > maxBytes) errs.push(`${f.name} is larger than ${maxSizeMB}MB`);
      else ok.push(f);
    });
    setErrors(errs);
    return ok;
  };

  const handleFiles = (list) => {
    const ok = validate(list);
    setFiles(ok);
    onFiles(ok);
  };

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setIsOver(true); }}
      onDragLeave={() => setIsOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setIsOver(false);
        handleFiles(e.dataTransfer.files);
      }}
      className={[
        "rounded-[12px] border-2 border-dashed p-6 text-center",
        isSurfaceTone
          ? "bg-[rgba(var(--afrimed-primary),0.05)] shadow-[0px_0px_0px_1px_rgba(var(--afrimed-primary),0.12)]"
          : "bg-white shadow-[0_1px_2px_rgba(16,24,40,0.06),0_8px_24px_rgba(16,24,40,0.06)]",
        isOver ? "bg-[rgba(77,163,255,0.06)]" : "",
        className,
      ].join(" ")}
      style={{
        borderColor: isSurfaceTone
          ? "rgba(var(--afrimed-primary), 0.24)"
          : "rgba(77,163,255,0.15)",
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={(e) => handleFiles(e.target.files)}
        className="hidden"
      />
      <div className="flex flex-col items-center gap-2">
        <img src="/icons/upload.svg" alt="" className="h-7 w-7 opacity-80" />
        <div className="text-body-large text-on-surface-var">
          Drag & drop file here <span className="mx-1">or</span>
          <button
            type="button"
            className="text-[rgb(var(--afrimed-primary))] no-underline hover:no-underline"
            onClick={() => inputRef.current?.click()}
          >
            Browse
          </button>
        </div>
        <div className="text-body-small text-on-surface-var opacity-70">
          Max {maxSizeMB}MB · Allowed: {accept}
        </div>
      </div>

      {/* File chips (removable) */}
      {files?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2" role="list" aria-label="Selected files">
          {files.map((f, i) => (
            <span
              key={`${f.name}-${i}`}
              role="listitem"
              className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm
                         bg-[rgba(77,163,255,0.12)] text-[rgb(var(--afrimed-primary))]"
            >
              {f.name}
              <button
                type="button"
                aria-label={`Remove ${f.name}`}
                className="rounded-full px-1.5 py-0.5 hover:bg-[rgba(0,0,0,.06)]"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const next = files.filter((_, idx) => idx !== i);
                  setFiles(next);                     // ⬅️ update local state
                  onFiles?.(next);                    // ⬅️ notify parent
                  if (next.length === 0 && inputRef.current) {
                    inputRef.current.value = "";      // ⬅️ allow re-uploading same file
                  }
                  if (next.length === 0) setErrors([]); // clear any size errors
                }}
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Compact list */}
      {files?.length > 0 && (
        <div className="mt-3 text-left">
          {files.map((f, i) => (
            <div key={`row-${f.name}-${i}`} className="text-body-medium text-on-surface flex items-center gap-2 py-1">
              <span className="inline-block w-2 h-2 rounded-full bg-[rgb(var(--afrimed-primary))]" />
              {f.name}
            </div>
          ))}
        </div>
      )}

      {/* Errors */}
      {errors.length > 0 && (
        <div className="mt-3 text-left">
          {errors.map((e, i) => (
            <div key={i} className="text-body-small text-error">• {e}</div>
          ))}
        </div>
      )}

      {children}
    </div>
  );
}
